/**
 * Deterministic hashing RNG. Every die value is a pure function of
 * (seed, ...keys) so games replay identically and Challenge mode can hand
 * every player the same roll matrix without storing it.
 */

function mix(h: number, k: number): number {
  h = Math.imul(h ^ k, 0x9e3779b1);
  h ^= h >>> 15;
  h = Math.imul(h, 0x85ebca77);
  h ^= h >>> 13;
  return h >>> 0;
}

export function hashKeys(seed: number, ...keys: number[]): number {
  let h = mix(0x811c9dc5, seed | 0);
  for (const k of keys) h = mix(h, k | 0);
  h = mix(h, keys.length + 1);
  return h;
}

/** Uniform integer in [1, faces]. */
export function rollValue(
  seed: number,
  faces: number,
  ...keys: number[]
): number {
  return (hashKeys(seed, ...keys) % faces) + 1;
}

/** Fresh random seed for a new game. */
export function newSeed(): number {
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return buf[0] ?? Math.floor(Math.random() * 0xffffffff);
  }
  return Math.floor(Math.random() * 0xffffffff);
}

/** Stateful PRNG (mulberry32) for AI sampling and UI animation jitter. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function shortId(len = 8): string {
  const alphabet = 'abcdefghijkmnpqrstuvwxyz23456789';
  let out = '';
  const buf = new Uint8Array(len);
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    crypto.getRandomValues(buf);
  } else {
    for (let i = 0; i < len; i++) buf[i] = Math.floor(Math.random() * 256);
  }
  for (let i = 0; i < len; i++)
    out += alphabet[(buf[i] ?? 0) % alphabet.length];
  return out;
}
